/**
 * "하루 N회까지만 노출" 정책을 스스로 관리하는 카운터.
 *
 * 안내 모달·툴팁처럼 한 번 보고 나면 성가신 UI가, 새로고침이나 화면 재진입 때마다
 * 다시 뜨지 않도록 브라우저(localStorage)에 노출 횟수를 적어 둔다. 저장값에 날짜를
 * 함께 적어 두므로 날짜가 바뀌면 카운트는 저절로 0부터 다시 시작한다.
 *
 * localStorage 를 못 쓰는 환경에서는 "제한 없음"으로 동작한다 — 안내 UI 때문에
 * 화면이 깨지는 것보다 몇 번 더 보이는 편이 낫다.
 */
export class DailyViewLimit {
  constructor(
    private readonly storageKey: string,
    private readonly maxPerDay: number,
  ) {}

  /**
   * 오늘 남은 노출 횟수가 있으면 1회 차감하고 `true`.
   * 노출 여부를 묻는 것과 기록을 남기는 것을 한 동작으로 묶어, 띄워 놓고 기록을
   * 빠뜨리는 실수를 막는다.
   */
  claim(): boolean {
    const shown = this.shownToday();
    if (shown >= this.maxPerDay) return false;
    this.write(shown + 1);
    return true;
  }

  /** "오늘 하루 보지 않기" — 남은 횟수를 모두 소진시킨다. */
  exhaust(): void {
    this.write(this.maxPerDay);
  }

  private shownToday(): number {
    const raw = this.read();
    if (!raw) return 0;

    const [date, count] = raw.split("/");
    if (date !== today()) return 0;

    const parsed = Number(count);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private read(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private write(count: number): void {
    try {
      localStorage.setItem(this.storageKey, `${today()}/${count}`);
    } catch {
      /* localStorage 불가 환경 무시 */
    }
  }
}

/** 로컬 시간 기준 날짜 키. UTC 를 쓰면 자정 전후로 하루가 어긋난다. */
const today = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};
